import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./OpenLiftCompatibility.css";

/**
 * OPENlift — 04 / Situations du quotidien
 * Nom du composant conservé pour préserver l'import dans Openlift.jsx.
 * Mise à jour : ajout de vraies images dans la zone haute des 3 cartes.
 */
const COPY = {
  fr: {
    topBrand: "OPENlift",
    topIndex: "SITUATIONS DU QUOTIDIEN",
    eyebrow: "À CHAQUE ÉTAPE, UNE MEILLEURE VISIBILITÉ",
    titleFirst: "Conçu pour les situations",
    titleSecond: "de vos équipes sur le terrain.",
    lead:
      "Trois contextes concrets. Une même ambition : prendre les décisions avec davantage d'informations.",
    situations: [
      {
        number: "01",
        category: "AVANT LE DÉPLACEMENT",
        title: "Préparez l’intervention.",
        description:
          "Consultez les événements disponibles pour mieux orienter les premières vérifications.",
        takeaway: "Une meilleure préparation",
        graphic: "preparation",
        image: "/images/openlift-daily/preparation-intervention.png",
        imageAlt:
          "Technicien préparant une intervention d'ascenseur devant un poste de travail et des écrans de supervision."
      },
      {
        number: "02",
        category: "PENDANT LE DIAGNOSTIC",
        title: "Explorez les pistes utiles.",
        description:
          "Avec OPENlift AI, l’assistance propose des pistes à vérifier par le technicien.",
        takeaway: "Une expertise augmentée",
        graphic: "diagnostic",
        image: "/images/openlift-daily/diagnostic-terrain.png",
        imageAlt:
          "Technicien d'ascenseur réalisant un diagnostic sur site avec une tablette devant une armoire de commande."
      },
      {
        number: "03",
        category: "AU NIVEAU DU PARC",
        title: "Repérez les récurrences.",
        description:
          "Identifiez les équipements à suivre et appuyez vos priorités sur des observations concrètes.",
        takeaway: "Une vue à l’échelle",
        graphic: "fleet",
        image: "/images/openlift-daily/supervision-parc.png",
        imageAlt:
          "Centre de supervision affichant l'analyse d'un parc d'ascenseurs sur plusieurs écrans."
      }
    ],
    distinctions: [
      "OPENlift : supervision et données de parc",
      "OPENlift AI : assistance à l'analyse",
      "Décision technique : professionnel habilité"
    ]
  },
  en: {
    topBrand: "OPENlift",
    topIndex: "EVERYDAY USE CASES",
    eyebrow: "BETTER VISIBILITY AT EVERY STAGE",
    titleFirst: "Built for the situations",
    titleSecond: "your teams face in the field.",
    lead:
      "Three real-world contexts. One goal: making decisions with better information.",
    situations: [
      {
        number: "01",
        category: "BEFORE THE VISIT",
        title: "Prepare the intervention.",
        description:
          "Review available events to guide the first checks more effectively.",
        takeaway: "Better preparation",
        graphic: "preparation",
        image: "/images/openlift-daily/preparation-intervention.png",
        imageAlt:
          "Technician preparing an elevator intervention from a workstation and monitoring screens."
      },
      {
        number: "02",
        category: "DURING DIAGNOSIS",
        title: "Explore useful leads.",
        description:
          "OPENlift AI suggests possible leads for the technician to verify.",
        takeaway: "Assisted expertise",
        graphic: "diagnostic",
        image: "/images/openlift-daily/diagnostic-terrain.png",
        imageAlt:
          "Elevator technician performing an on-site diagnostic with a tablet next to a control cabinet."
      },
      {
        number: "03",
        category: "ACROSS THE FLEET",
        title: "Spot recurring issues.",
        description:
          "Identify equipment to monitor and base your priorities on concrete observations.",
        takeaway: "A fleet-wide view",
        graphic: "fleet",
        image: "/images/openlift-daily/supervision-parc.png",
        imageAlt:
          "Operations center showing fleet-wide elevator monitoring on multiple screens."
      }
    ],
    distinctions: [
      "OPENlift: fleet data and supervision",
      "OPENlift AI: analytical assistance",
      "Technical decisions: qualified professional"
    ]
  }
};

function CardBadge({ kind }) {
  if (kind === "preparation") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 3.75h10a1.75 1.75 0 0 1 1.75 1.75v14.25A1.75 1.75 0 0 1 17 21.5H7A1.75 1.75 0 0 1 5.25 19.75V5.5A1.75 1.75 0 0 1 7 3.75Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8.75 8.25h6.5M8.75 11.5h6.5M8.75 14.75h4.25" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M9.5 3.75v-1h5v1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (kind === "diagnostic") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="5.75" stroke="currentColor" strokeWidth="1.8" />
        <path d="m15 15 4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3.5 15.75h4l3-6 3.25 10 3-6h3.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SituationCard({ item }) {
  return (
    <article className={`ol-uses__card ol-uses__card--${item.graphic}`}>
      <div className="ol-uses__card-visual">
        <img className="ol-uses__card-image" src={item.image} alt={item.imageAlt} loading="lazy" />
        <div className="ol-uses__card-overlay" aria-hidden="true" />
        <span className="ol-uses__card-number">{item.number}</span>
        <div className="ol-uses__card-badge" aria-hidden="true">
          <CardBadge kind={item.graphic} />
        </div>
      </div>

      <div className="ol-uses__card-content">
        <p className="ol-uses__card-category">{item.category}</p>
        <h3 className="ol-uses__card-title">{item.title}</h3>
        <p className="ol-uses__card-description">{item.description}</p>
        <p className="ol-uses__card-takeaway">
          {item.takeaway}
          <span aria-hidden="true"> →</span>
        </p>
      </div>
    </article>
  );
}

export default function OpenLiftCompatibility() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = COPY[language] || COPY.fr;
  const sectionRef = useRef(null);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          element.classList.add("ol-uses--visible");
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="ol-uses" ref={sectionRef} aria-labelledby="ol-uses-title">
      <div className="ol-uses__container">
        <div className="ol-uses__overline" aria-hidden="true">
          <span>{content.topBrand}</span>
          <span>{content.topIndex}</span>
        </div>

        <header className="ol-uses__header">
          <p className="ol-uses__eyebrow">{content.eyebrow}</p>
          <h2 id="ol-uses-title" className="ol-uses__title">
            <span>{content.titleFirst}</span>
            <span className="ol-uses__title-accent">{content.titleSecond}</span>
          </h2>
          <p className="ol-uses__lead">{content.lead}</p>
        </header>

        <div className="ol-uses__grid">
          {content.situations.map((item) => (
            <SituationCard key={item.number} item={item} />
          ))}
        </div>

        <ul
          className="ol-uses__distinctions"
          aria-label={language === "en" ? "Product roles" : "Rôle des solutions et responsabilités"}
        >
          {content.distinctions.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
